/**
 * ChangeSummary — compact delta summary when 2+ snapshots exist.
 * Shows finish delta, critical count change, open ends change, and top WBS movers.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import type { ChangeSummaryData } from '@/types/cockpit';

export interface ChangeSummaryProps {
  data: ChangeSummaryData | null;
  className?: string;
}

function signedNum(v: number): string {
  if (v > 0) return `+${v}`;
  return String(v);
}

export const ChangeSummary: React.FC<ChangeSummaryProps> = ({ data, className }) => {
  // No multi-snapshot data
  if (!data) {
    return (
      <div className={cn('rounded-lg border bg-card p-4 text-center', className)}>
        <p className="text-sm font-medium text-muted-foreground">What Changed</p>
        <p className="text-[11px] text-muted-foreground mt-1">
          Import another snapshot to enable change detection.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card p-4', className)}>
      <h3 className="text-sm font-medium text-foreground mb-3">What Changed</h3>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="flex flex-col">
          <span className="text-[11px] text-muted-foreground">Finish moved</span>
          <span className={cn(
            'text-lg font-bold',
            data.finishDeltaDays > 0 ? 'text-red-500' : data.finishDeltaDays < 0 ? 'text-emerald-500' : 'text-muted-foreground',
          )}>
            {signedNum(data.finishDeltaDays)}d
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] text-muted-foreground">Critical count</span>
          <span className={cn(
            'text-lg font-bold',
            data.criticalDelta > 0 ? 'text-red-500' : data.criticalDelta < 0 ? 'text-emerald-500' : 'text-muted-foreground',
          )}>
            {signedNum(data.criticalDelta)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] text-muted-foreground">Open ends</span>
          <span className={cn(
            'text-lg font-bold',
            data.openEndsDelta > 0 ? 'text-red-500' : data.openEndsDelta < 0 ? 'text-emerald-500' : 'text-muted-foreground',
          )}>
            {signedNum(data.openEndsDelta)}
          </span>
        </div>
      </div>

      {/* WBS movers */}
      {data.wbsMovers.length > 0 && (
        <div>
          <h4 className="text-[11px] font-medium text-muted-foreground mb-1">Top WBS Risk Movers</h4>
          <ul className="space-y-0.5">
            {data.wbsMovers.map((m) => (
              <li key={m.id} className="flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground truncate max-w-[200px]">{m.name}</span>
                <span className={cn(
                  'font-medium',
                  m.riskDelta > 0 ? 'text-red-500' : 'text-emerald-500',
                )}>
                  {signedNum(m.riskDelta)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
